// Shared with linq-server (copied by its `npm run copy`), so it must only import from this folder and uuid.
//
// An organisation and all its models as one JSON file, for LINQ administrators to move it between organisations or
// environments. The file holds the organisation's content: its models, projects, tags, information assets, emission
// factors and shared filters, and every node and edge. It leaves out anything about people (who has access, who owns a
// model, private filters, who to notify), billing and CRM details, backups, and the calculated insights, which are
// worked out again once it is imported.
import { v4 } from "uuid"
import { LinqsetEdge, LinqsetNode } from "./linqset-base"
import { InsightsFilter, ModelDetails, Organisation, Project } from "./organisation"

export const ORGANISATION_EXPORT_FORMAT = "linq-organisation"
export const ORGANISATION_EXPORT_VERSION = 1

// the organisation's content that moves with it
export type ExportedOrganisation = Pick<Organisation, 'name' | 'industry' | 'currencyCode' | 'defaultCapacityHours' | 'model' | 'models' | 'projects' |
    'tagGroups' | 'modelTags' | 'modelPreferences' | 'emissionFactors' | 'informationAssets' | 'insightsFilters'> & { _key: string }

export interface OrganisationExport {
    format: typeof ORGANISATION_EXPORT_FORMAT
    version: number
    serverVersion: string           // the LINQ version it was exported from
    exportedDate: Date
    organisation: ExportedOrganisation
    nodes: LinqsetNode[]
    edges: LinqsetEdge[]
}

// what a database document carries that isn't the data: its id and revision are made again when it is saved
const withoutDatabaseFields = <T>(doc: T): T => {
    const { _id, _rev, ...rest } = doc as any
    return rest as T
}

const exportModel = (m: ModelDetails): ModelDetails => {
    const { insights, userAccess, backups, owner, changes, role, ...rest } = m as any
    return { ...rest, insights: [], userAccess: [], backups: [], changes: [], useOrganisationAccess: true } as ModelDetails
}

const exportProject = (p: Project): Project => {
    const { userAccess, role, ...rest } = p as any
    return { ...rest, userAccess: [], useOrganisationAccess: true } as Project
}

// an organisation and its graph as the file exports it
export const createOrganisationExport = (organisation: Organisation, nodes: LinqsetNode[], edges: LinqsetEdge[], serverVersion: string): OrganisationExport => ({
    format: ORGANISATION_EXPORT_FORMAT,
    version: ORGANISATION_EXPORT_VERSION,
    serverVersion,
    exportedDate: new Date(),
    organisation: {
        _key: organisation._key,
        name: organisation.name,
        industry: organisation.industry,
        currencyCode: organisation.currencyCode,
        defaultCapacityHours: organisation.defaultCapacityHours,
        model: organisation.model && exportModel(organisation.model),
        models: (organisation.models || []).map(exportModel),
        projects: (organisation.projects || []).map(exportProject),
        tagGroups: organisation.tagGroups || [],
        modelTags: organisation.modelTags,
        modelPreferences: organisation.modelPreferences,
        emissionFactors: organisation.emissionFactors || [],
        informationAssets: organisation.informationAssets || [],
        // only the shared ones: a private filter is someone's own, and its owner isn't moving with it
        insightsFilters: (organisation.insightsFilters || []).filter((f: InsightsFilter) => !f.private).map(f => ({ ...f, owner: undefined })),
    },
    nodes: nodes.map(n => {
        const { insights, userKeys, excluded, properties, currentState, ...rest } = withoutDatabaseFields(n) as any
        return { ...rest, userKeys: [] } as LinqsetNode
    }),
    edges: edges.map(e => {
        const { proportion, processed, excluded, ...rest } = withoutDatabaseFields(e) as any
        return rest as LinqsetEdge
    }),
})

// what is wrong with a file offered for import, if anything
export const checkOrganisationExport = (data: any): string | undefined => {
    if (!data || typeof data !== 'object') return "The file isn't an organisation export."
    if (data.format !== ORGANISATION_EXPORT_FORMAT) return "The file isn't a LINQ organisation export."
    if (typeof data.version !== 'number' || data.version > ORGANISATION_EXPORT_VERSION) return "The file was exported from a newer version of LINQ."
    const o = data.organisation
    if (!o || typeof o !== 'object' || typeof o._key !== 'string' || !Array.isArray(o.models) || !Array.isArray(o.projects)) return "The file has no organisation in it."
    if (!Array.isArray(data.nodes) || !Array.isArray(data.edges)) return "The file has no models in it."
    if (data.nodes.some((n: any) => !n || typeof n._key !== 'string')) return "Some of the nodes in the file have no key."
    if (data.edges.some((e: any) => !e || typeof e._key !== 'string' || typeof e.sourceKey !== 'string' || typeof e.targetKey !== 'string')) return "Some of the edges in the file aren't complete."
    return undefined
}

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi

// every key in the export, made new: the organisation's becomes the one given, and every other one (models, projects,
// nodes, edges, tags, assets, factors, filters and the references between them, wherever they are) a new one, the
// same new one each time it appears. So the import can sit beside the organisation it came from, even in the same
// database. The fixed keys (current state, the organisation model, model tags) aren't ids, so stay as they are
export const rekeyOrganisationExport = (data: OrganisationExport, organisationKey: string, newKey: () => string = v4): OrganisationExport => {
    const keys = new Map<string, string>([[data.organisation._key.toLowerCase(), organisationKey]])
    const swap = (text: string) => text.replace(UUID, id => {
        const lower = id.toLowerCase()
        let next = keys.get(lower)
        if (!next) {
            next = newKey()
            keys.set(lower, next)
        }
        return next
    })
    const walk = (value: any): any => {
        if (typeof value === 'string') return swap(value)
        if (Array.isArray(value)) return value.map(walk)
        if (value instanceof Date) return value
        if (value && typeof value === 'object') {
            return Object.fromEntries(Object.entries(value).map(([k, v]) => [swap(k), walk(v)]))
        }
        return value
    }
    return walk(data)
}

// an organisation with no models, projects or nodes, so an import can fill it
export const isEmptyOrganisation = (organisation: Organisation, nodeCount: number) =>
    nodeCount === 0 && !(organisation.models || []).length && !(organisation.projects || []).length

// the organisation's content from an import, put onto the organisation it goes into (its key, name, access, billing
// and the rest of its own details stay as they are)
export const applyOrganisationExport = (organisation: Organisation, data: OrganisationExport): Organisation => {
    const o = data.organisation
    return {
        ...organisation,
        industry: o.industry ?? organisation.industry,
        currencyCode: o.currencyCode ?? organisation.currencyCode,
        defaultCapacityHours: o.defaultCapacityHours ?? organisation.defaultCapacityHours,
        model: o.model ?? organisation.model,
        models: o.models || [],
        projects: o.projects || [],
        tagGroups: o.tagGroups || [],
        modelTags: o.modelTags ?? organisation.modelTags,
        modelPreferences: o.modelPreferences ?? organisation.modelPreferences,
        emissionFactors: o.emissionFactors || [],
        informationAssets: o.informationAssets || [],
        insightsFilters: o.insightsFilters || [],
    }
}
